/**
 * AmbientAudioSystem - Manages looping ambient sound layers based on player position.
 *
 * Three ambient layers, one per floor:
 *   Floor 1 (y   0–200)  — AMBIENT_ROOM    (soft electrical hum)
 *   Floor 2 (y 300–500)  — AMBIENT_VENT    (airy corridor whisper)
 *   Floor 3 (y 600–900)  — AMBIENT_MACHINE (mechanical workshop thud)
 *
 * The active layer crossfades in; the previous layer fades out.
 */

import { audioManager } from '../../engine/AudioManager';
import { SFX } from '../audio/SoundEffects';

// One layer per floor
const FLOOR_AMBIENT: SFX[] = [SFX.AMBIENT_ROOM, SFX.AMBIENT_VENT, SFX.AMBIENT_MACHINE];

// Y thresholds — must match TestScene floor layout
const FLOOR_Y_RANGES: Array<{ min: number; max: number }> = [
  { min: -200, max: 200  }, // Floor 1 (public)
  { min:  200, max: 600  }, // Floor 2 (employee)
  { min:  600, max: 1100 }, // Floor 3 (restricted)
];

const FADE_MS = 1200; // crossfade duration

export class AmbientAudioSystem {
  private activeFloor: number = -1;
  private activeSoundIds: Map<SFX, number | null> = new Map();
  private destroyed: boolean = false;

  constructor() {
    // Start all ambient loops stopped
    FLOOR_AMBIENT.forEach(sfx => this.activeSoundIds.set(sfx, null));
  }

  /**
   * Call once per frame (or less often — every 500ms is fine).
   * @param playerY  World-space Y of the player
   */
  public update(playerY: number): void {
    if (this.destroyed) return;

    const floor = this.getFloor(playerY);
    if (floor === this.activeFloor) return; // no change

    this.crossfadeTo(floor);
    this.activeFloor = floor;
  }

  private getFloor(y: number): number {
    for (let i = 0; i < FLOOR_Y_RANGES.length; i++) {
      const range = FLOOR_Y_RANGES[i]!;
      if (y >= range.min && y < range.max) return i;
    }
    return 0; // default floor 1
  }

  private crossfadeTo(floor: number): void {
    const sfxPool = (audioManager as any).sfxPool as Map<string, any>;

    // Stop old layer
    const oldSfx = floor >= 0 && this.activeFloor >= 0 ? FLOOR_AMBIENT[this.activeFloor] : null;
    if (oldSfx) {
      const oldId = this.activeSoundIds.get(oldSfx);
      const oldHowl = sfxPool.get(oldSfx);
      if (oldHowl && oldId !== null && oldId !== undefined) {
        oldHowl.fade(oldHowl.volume(oldId), 0, FADE_MS, oldId);
        const id = oldId;
        setTimeout(() => { try { oldHowl.stop(id); } catch {} }, FADE_MS + 50);
        this.activeSoundIds.set(oldSfx, null);
      }
    }

    if (floor < 0) return;

    // Start new layer
    const newSfx = FLOOR_AMBIENT[floor]!;
    const newHowl = sfxPool.get(newSfx);
    if (!newHowl) return;

    try {
      const id = newHowl.play();
      newHowl.loop(true, id);
      newHowl.volume(0, id);
      newHowl.fade(0, newHowl._volume ?? 0.05, FADE_MS, id);
      this.activeSoundIds.set(newSfx, id);
    } catch {
      // Sound not loaded yet — silently skip
    }
  }

  /** Stop all ambient loops immediately */
  public stopAll(): void {
    const sfxPool = (audioManager as any).sfxPool as Map<string, any>;
    FLOOR_AMBIENT.forEach(sfx => {
      const id = this.activeSoundIds.get(sfx);
      const howl = sfxPool.get(sfx);
      if (howl && id !== null && id !== undefined) {
        try { howl.stop(id); } catch {}
      }
      this.activeSoundIds.set(sfx, null);
    });
    this.activeFloor = -1;
  }

  public destroy(): void {
    this.stopAll();
    this.destroyed = true;
  }
}
